import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react';
import { useAppStore } from '../../state/store';
import {
  useSeriesOptions,
  filterSeriesOptionsByEmittedFields,
} from '../../utils/useSeriesOptions';
import { InstrumentPill, ValuePill, FromIndicatorBadge } from './IndicatorPreviewFields';

const OHLC_FIELDS = ['High', 'Low', 'Close'];

export type IndicatorEditorADXRef = {
  getConfig: () => {
    instrumentId: string;
    period: number;
    lag: number;
  };
};

type Props = {
  config: {
    instrumentId: string;
    period?: number;
    lag?: number;
  };
  onChange: (config: Props['config']) => void;
  editMode: boolean;
  indicatorId?: string;
};

const IndicatorEditorADX = forwardRef<IndicatorEditorADXRef, Props>(
  ({ config, onChange, editMode, indicatorId }, ref) => {
    const [period, setPeriod] = useState(config.period ?? 14);
    const [lag, setLag] = useState(config.lag ?? 0);

    const allSeriesOptions = useSeriesOptions(indicatorId);
    const ohlcOptions = filterSeriesOptionsByEmittedFields(
      allSeriesOptions,
      OHLC_FIELDS
    );
    const hasSingleOption = ohlcOptions.length === 1;

    useEffect(() => {
      if (hasSingleOption && config.instrumentId !== ohlcOptions[0].id) {
        onChange({ ...config, instrumentId: ohlcOptions[0].id });
      }
    }, [hasSingleOption, ohlcOptions, config.instrumentId, onChange]);

    const selectedSeries = ohlcOptions.find((s) => s.id === config.instrumentId);
    const validInstrumentId =
      selectedSeries?.id ?? ohlcOptions[0]?.id ?? config.instrumentId;

    useEffect(() => {
      if (ohlcOptions.length > 0 && !ohlcOptions.some((o) => o.id === config.instrumentId)) {
        onChange({ ...config, instrumentId: ohlcOptions[0].id });
      }
    }, [ohlcOptions, config.instrumentId, config, onChange]);

    const handleSeriesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange({ ...config, instrumentId: e.target.value });
    };

    const handlePeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value);
      if (!isNaN(val) && val >= 2) {
        setPeriod(val);
        onChange({ ...config, period: val });
      }
    };

    const handleLagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value);
      if (!isNaN(val) && val >= 0) {
        setLag(val);
        onChange({ ...config, lag: val });
      }
    };

    useImperativeHandle(ref, () => ({
      getConfig: () => ({
        instrumentId: validInstrumentId,
        period,
        lag,
      }),
    }));

    if (!editMode) {
      const inst = useAppStore
        .getState()
        .instruments.find((i) => i.id === validInstrumentId);
      const symbol = inst?.symbol ?? selectedSeries?.label ?? '???';
      return (
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <InstrumentPill>{symbol}</InstrumentPill>
          <ValuePill label="Period" value={period} />
          {lag > 0 && <ValuePill label="Lag" value={lag} />}
          {selectedSeries?.type === 'indicator' && <FromIndicatorBadge />}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-4 items-center">
        {!hasSingleOption && ohlcOptions.length > 0 && (
          <label>
            Series:
            <select
              value={validInstrumentId}
              onChange={handleSeriesChange}
              className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
            >
              {ohlcOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label} {opt.type === 'indicator' ? '↔' : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        <label>
          Period:
          <input
            type="number"
            min="2"
            value={period}
            onChange={handlePeriodChange}
            className="ml-2 w-20 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
          />
        </label>

        <label>
          Lag:
          <input
            type="number"
            min="0"
            value={lag}
            onChange={handleLagChange}
            className="ml-2 w-20 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
          />
        </label>
      </div>
    );
  }
);

IndicatorEditorADX.displayName = 'IndicatorEditorADX';

export default IndicatorEditorADX;
